import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  buildCommentThreads,
  commentAuthorName,
  replyTargetNameDative,
  repliesPreviewCount,
  repliesWord,
} from "../../../utils/clubLifeCommentsUtils";
import { personDisplayName } from "../../../utils/clientAppUtils";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { clientMemberProfileLink } from "../../../utils/socialProfileLink";
import { Avatar } from "../../ui";
import s from "../clinic.module.css";

function clientGlobal(modClass, globalName, mode) {
  return mode === "client" || mode === "admin"
    ? `${modClass} ${globalName}`
    : modClass;
}

function CommentRow({ comment, isReply, mode, onReply, linkProfiles }) {
  const authorLabel = commentAuthorName(comment.author);
  const targetLabel = isReply ? replyTargetNameDative(comment.parent?.author) : null;
  const authorLink =
    linkProfiles && mode === "client"
      ? clientMemberProfileLink(comment.author)
      : null;

  const avatar = (
    <Avatar
      name={personDisplayName(comment.author?.full_name) || "?"}
      src={mediaUrl(comment.author?.photo_url)}
      size="sm"
    />
  );

  const authorNameEl = authorLink ? (
    <Link to={authorLink} className={s.clubLifeCommentAuthorLink}>
      <strong className={s.clubLifeCommentAuthor}>{authorLabel}</strong>
    </Link>
  ) : (
    <strong className={s.clubLifeCommentAuthor}>{authorLabel}</strong>
  );

  return (
    <li
      className={`${s.clubLifeCommentItem} ${
        isReply ? s.clubLifeCommentItemReply : ""
      }`}
    >
      {authorLink ? (
        <Link
          to={authorLink}
          className={s.clubLifeCommentAvatarLink}
          aria-label={`Профиль: ${authorLabel}`}
        >
          {avatar}
        </Link>
      ) : (
        avatar
      )}
      <div className={s.clubLifeCommentBody}>
        <div className={clientGlobal(s.clubLifeCommentMeta, "clubLifeCommentMeta", mode)}>
          {authorNameEl}
          {targetLabel ? (
            <span className={clientGlobal(s.clubLifeCommentReplyTo, "clubLifeCommentReplyTo", mode)}>
              → {targetLabel}
            </span>
          ) : null}
        </div>
        <div className={s.clubLifeCommentBubble}>
          <span>{comment.body}</span>
        </div>
        {onReply ? (
          <button
            type="button"
            className={s.clubLifeCommentReplyBtn}
            onClick={() => onReply(comment)}
          >
            Ответить
          </button>
        ) : null}
      </div>
    </li>
  );
}

function CommentThread({ thread, mode, onReply, linkProfiles }) {
  const { comment, replies } = thread;
  const previewCount = repliesPreviewCount(replies.length);
  const [expanded, setExpanded] = useState(replies.length <= 2);
  const hiddenCount = Math.max(0, replies.length - previewCount);
  const visibleReplies = expanded ? replies : replies.slice(0, previewCount);

  return (
    <li className={s.clubLifeCommentThread}>
      <ul className={s.clubLifeCommentList}>
        <CommentRow comment={comment} isReply={false} mode={mode} onReply={onReply} linkProfiles={linkProfiles} />
        {visibleReplies.map((reply) => (
          <CommentRow
            key={reply.id}
            comment={reply}
            isReply
            mode={mode}
            onReply={onReply}
            linkProfiles={linkProfiles}
          />
        ))}
      </ul>
      {replies.length > previewCount ? (
        <button
          type="button"
          className={clientGlobal(
            s.clubLifeRepliesToggle,
            "clubLifeRepliesToggle",
            mode,
          )}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? "Скрыть ответы"
            : hiddenCount === 1
              ? "Показать ещё 1 ответ"
              : `Показать ещё ${hiddenCount} ${repliesWord(hiddenCount)}`}
        </button>
      ) : null}
    </li>
  );
}

export default function ClubLifeCommentThreads({
  comments,
  mode,
  onReply,
  linkProfiles = false,
}) {
  const threads = useMemo(() => buildCommentThreads(comments), [comments]);

  if (!threads.length) return null;

  return (
    <ul className={clientGlobal(s.clubLifeCommentThreads, "clubLifeCommentThreads", mode)}>
      {threads.map((thread) => (
        <CommentThread
          key={thread.comment.id}
          thread={thread}
          mode={mode}
          onReply={onReply}
          linkProfiles={linkProfiles}
        />
      ))}
    </ul>
  );
}
